import React, { FunctionComponent, useEffect, useState, useMemo, useCallback } from 'react';
import { Form, Formik } from 'formik';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import * as Yup from 'yup';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import InputField from '../../presentation/InputField';
import SearchIcon from '../../../../productCatalog/assets/filters/SearchIcon';
import { postcodeSchema } from '../../../../productCatalog/service/formValidation';
import { IFormData } from '../PostcodeCheckContainer';
import postcodeFormStyles from './postcodeFormStyles';
import Grid from '@material-ui/core/Grid/Grid';
import { Button } from '@material-ui/core';
import Loader from '../../presentation/Loader';
import OnSubmitValidationError from './OnSubmitValidationError';
import usePlatformConfig from '../../../react/usePlatformConfig';
import { DEFAULT_DEALER_FILE } from '../../../dealerList/dealersConstants';
import errorHandler from '../../../service/errorHandler';
import numberUtilities from '../../../../../../common/utilities/numberUtilities';
import { ISpcTheme } from '../../../theme/ISpcTheme';
import Translation from '../../translation/Translation';

export interface IPostcodeSchemaTranslations {
  required: string;
  matches: string;
}

export interface IPostcodeFormValues {
  postcode: string;
}

interface IPostcodeForm {
  handleFormSubmission: (data: IFormData) => void;
  handleFormValidationError: () => void;
  isDealerSearchError: boolean;
  onDealerSearchError: (hasError: boolean) => void;
}

const PostcodeForm: FunctionComponent<IPostcodeForm & WrappedComponentProps> = props => {
  const { intl, handleFormSubmission, handleFormValidationError, isDealerSearchError, onDealerSearchError } = props;
  const styles = postcodeFormStyles();
  const theme = useTheme<ISpcTheme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const {
    platformConfig: { spcDealersFileName },
  } = usePlatformConfig();
  const [postcodes, setPostcodes] = useState<number[]>([]);

  const getDealersPostcodes: () => void = useCallback(() => {
    import(
      /* webpackChunkName: "postcodes.[request]" */ `../../../../common/dealerList/POSTCODES_${spcDealersFileName}.json`
    )
      .then(postcodes => setPostcodes(numberUtilities.toNumberArray(Object.values<string>(postcodes))))
      .catch(() => {
        errorHandler.handleError(
          `Dealer postcodes file import failed. spcDealersFileName value -> ${spcDealersFileName}`,
        );
        import(
          /* webpackChunkName: "postcodes.[request]" */ `../../../../common/dealerList/POSTCODES_${DEFAULT_DEALER_FILE}.json`
        )
          .then(postcodes => setPostcodes(numberUtilities.toNumberArray(Object.values<string>(postcodes))))
          .catch(err => errorHandler.handleError(`Dealers file import error -> ${err}`));
      });
  }, []);

  const translationsObject: IPostcodeSchemaTranslations = useMemo(() => {
    return {
      required: intl.formatMessage({ id: 'spc.dealer.form.validation.required' }),
      matches: intl.formatMessage({ id: 'spc.dealer.form.validation.matches' }),
    };
  }, [intl]);
  const validation: Yup.Schema<IPostcodeFormValues> = useMemo(
    () => postcodeSchema(translationsObject),
    [translationsObject, postcodes],
  );

  useEffect(() => {
    getDealersPostcodes();
  }, []);

  if (!postcodes) {
    return <Loader />;
  }

  return (
    <Formik
      initialValues={{
        postcode: '',
      }}
      onSubmit={handleFormSubmission}
      validationSchema={validation}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ touched, errors, values, handleChange, submitCount, isSubmitting, isValid }) => (
        <Form className={styles.formContainer}>
          <OnSubmitValidationError
            submitCount={submitCount}
            isSubmitting={isSubmitting}
            isValid={isValid}
            callback={handleFormValidationError}
          />
          <Grid container={true} wrap={'nowrap'}>
            <InputField
              required={false}
              name="postcode"
              type="string"
              placeholder={intl.formatMessage({ id: 'spc.dealer.form.placeholder' })}
              prefix={<SearchIcon />}
              data-test="input:postcode"
              color="primary"
              isDealerSearchError={isDealerSearchError}
              validation={{ touched, errors }}
              value={values.postcode}
              onDealerSearchError={onDealerSearchError}
              onChange={handleChange}
              className={styles.searchField}
            />
            <Button
              className={` ${styles.submit} ${isMobile ? styles.submitMobile : ''}`}
              type="submit"
              data-test={'button:postcode'}
            >
              <Translation id="spc.dealer.form.submit" />
            </Button>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default injectIntl(PostcodeForm);
